<template>
    <div class="contact-phone-search" :class="{ 'is-dark': dark }">
        <template v-if="phoneChoice">
            <button type="button" class="zs-btn zs-btn--sm back-btn" @click="phoneChoice = undefined">
                <i class="fa-solid fa-chevron-left" />
                <span>Back to results</span>
            </button>
            <p class="choose-header">Choose a number for {{ phoneChoice.contactName ?? 'this contact' }}:</p>
            <div class="results">
                <button v-for="phone in phoneChoice.phones" :key="phone.id" type="button" class="row" @click="selectPhone(phone.phone)">
                    <span class="row-text">
                        <span class="row-title">{{ $filters.phone(phone.phone) }}</span>
                        <span v-if="phone.label" class="row-sub">{{ phone.label }}</span>
                    </span>
                </button>
            </div>
        </template>

        <template v-else>
            <div class="zs-input">
                <i class="fa-solid fa-magnifying-glass" />
                <input v-model="query" v-autofocus="autofocus" type="text" placeholder="Search contacts" />
            </div>

            <div class="results">
                <Loader v-if="isSearching" />
                <ErrorState v-else-if="loadError" :message="loadError" :retry="search" />
                <p v-else-if="!visibleContacts?.length" class="empty">{{ query ? 'No contacts match your search.' : 'No contacts yet.' }}</p>
                <template v-else>
                    <button v-for="contact in visibleContacts" :key="contact.id" type="button" class="row" @click="onContactClick(contact)">
                        <Avatar :contact="contact" size="sm" />
                        <span class="row-text">
                            <span class="row-title">
                                {{ crmGetContactDisplayName(contact) ?? $filters.phone(contact.phone) ?? contact.email ?? 'Unnamed contact' }}
                            </span>
                            <span v-if="contact.phone" class="row-sub">{{ $filters.phone(contact.phone) }}</span>
                        </span>
                    </button>
                </template>
            </div>
        </template>
    </div>
</template>

<script lang="ts" setup>
import { dataFrom } from '@zyno-io/openapi-client-codegen';
import { formatError, handleError, showAlert } from '@zyno-io/vue-foundation';
import { computed, onMounted, ref, watch } from 'vue';

import { type GetCrmContactsIndexResponse, SuiteCrmContactsApi } from '@/openapi-clients-generated/suite';
import Avatar from '@/shared/components/avatar.vue';
import ErrorState from '@/shared/components/error-state.vue';
import Loader from '@/shared/components/loader.vue';

import { crmGetContactDisplayName } from './contact.helpers';

type ContactRow = GetCrmContactsIndexResponse[number];

export interface IContactPhoneSelection {
    contactId: string;
    contactName: string | null;
    phone: string;
}

export interface IContactSelection {
    contactId: string;
    contactName: string | null;
}

const props = withDefaults(
    defineProps<{
        /**
         * `phone` resolves the chosen contact's numbers and emits one (with a chooser when it has
         * several); `contact` emits the contact itself — no phone lookup, and a contact with no
         * numbers is a valid pick.
         */
        mode?: 'phone' | 'contact';
        /** Render for a dark surface (the softphone dialer) instead of the standard light one. */
        dark?: boolean;
        autofocus?: boolean;
    }>(),
    { mode: 'phone', dark: false, autofocus: true }
);

const emit = defineEmits<{ select: [selection: IContactPhoneSelection]; selectContact: [selection: IContactSelection] }>();

const SEARCH_DEBOUNCE_MS = 300;

const query = ref('');
const contacts = ref<ContactRow[]>();
const isSearching = ref(false);
const loadError = ref<string>();
// Plain setTimeout rather than a lodash debounce: the visual-test harness freezes Date.now(), which
// lodash's timers key off, so a debounced search would never run there.
let searchTimer: ReturnType<typeof setTimeout> | undefined;
let searchSeq = 0;

async function search() {
    const seq = ++searchSeq;
    isSearching.value = true;
    loadError.value = undefined;
    try {
        const result = dataFrom(await SuiteCrmContactsApi.getCrmContactsIndex({ query: { q: query.value.trim() || undefined } }));
        if (searchSeq !== seq) return;
        contacts.value = result;
    } catch (err) {
        if (searchSeq !== seq) return;
        handleError(err);
        // Clear stale results: leaving the previous set (or "no matches") on screen reads as a
        // definitive answer to a search that actually failed.
        contacts.value = undefined;
        loadError.value = formatError(err);
    } finally {
        if (searchSeq === seq) isSearching.value = false;
    }
}

// Phone mode exists to produce a number, so a contact with none is dead weight in the list — it can
// only ever answer "this contact has no phone numbers".
const visibleContacts = computed(() => (props.mode === 'phone' ? contacts.value?.filter(c => c.phone) : contacts.value));

watch(query, () => {
    clearTimeout(searchTimer);
    isSearching.value = true;
    searchTimer = setTimeout(search, SEARCH_DEBOUNCE_MS);
});
onMounted(search);

interface IPhoneChoice {
    contactId: string;
    contactName: string | null;
    phones: { id: string; phone: string; label: string | null }[];
}
const phoneChoice = ref<IPhoneChoice>();

// Guards a double-click: the selection flow is async, and running it twice would emit two
// selections (dismissing the host modal mid-flight).
let isSelecting = false;

async function onContactClick(contact: ContactRow) {
    if (isSelecting) return;
    isSelecting = true;
    const contactName = crmGetContactDisplayName(contact);

    // Contact-picking wants the contact, not one of its numbers — skip the detail fetch entirely.
    if (props.mode === 'contact') {
        emit('selectContact', { contactId: contact.id, contactName });
        isSelecting = false;
        return;
    }

    try {
        const full = dataFrom(await SuiteCrmContactsApi.getCrmContactsShow({ path: { id: contact.id } }));
        // Only E.164 numbers are dialable/textable here — the index row's `phone` is a preview and
        // the contact may hold several.
        const phones = full.phones.filter(p => p.phone.startsWith('+'));
        if (!phones.length) {
            return showAlert('No phone number', 'This contact has no phone numbers.');
        }
        if (phones.length === 1) {
            return emit('select', { contactId: contact.id, contactName, phone: phones[0]!.phone });
        }
        phoneChoice.value = {
            contactId: contact.id,
            contactName,
            phones: phones.map(p => ({ id: p.id, phone: p.phone, label: p.typeCustom ?? p.type }))
        };
    } catch (err) {
        handleError(err);
        showAlert('Contact', formatError(err));
    } finally {
        isSelecting = false;
    }
}

function selectPhone(phone: string) {
    emit('select', { contactId: phoneChoice.value!.contactId, contactName: phoneChoice.value!.contactName, phone });
}
</script>

<style scoped>
@reference '#tw.css';

.contact-phone-search {
    @apply flex flex-col min-h-0 gap-2;
}

.results {
    @apply flex flex-col min-h-0 overflow-y-auto;
}

/* `height` and `justify-start` override the global <button> reset (fixed control height, centered
   content) — rows are two lines tall and left-aligned. */
.row {
    @apply flex items-center justify-start gap-3 w-full px-2 py-2 bg-transparent border-none rounded cursor-pointer text-left;
    height: auto;
}

.row:hover {
    background: var(--surface-2);
}

.row-text {
    @apply flex flex-col items-start gap-0.5 min-w-0;
}

.row-title {
    @apply font-semibold truncate max-w-full;
}

.row-sub,
.empty {
    color: var(--ink-3);
    font-size: var(--t-sm);
}

.empty {
    @apply text-center py-6;
}

.back-btn,
.choose-header {
    @apply self-start;
}

/* Dark surface (softphone dialer): the shared inputs/rows assume the light app background. */
.is-dark .row:hover {
    @apply bg-chrome-panel-raised;
}

.is-dark .row-title {
    @apply text-white;
}

.is-dark .row-sub,
.is-dark .empty {
    @apply text-ink-on-chrome-2;
}
</style>
